import { type HttpPost, mapServerError } from '@/shared/infra/http'
import type { ExtractVideoPosterFrame, UploadToSignedUrl } from '@/shared/infra/files'
import type { GalleryPhoto } from '@/shared/domain'
import type { UploadGalleryVideo } from '@/modules/gallery/use-cases'

type UploadUrlArtifact = {
  storagePath: string
  signedUrl: string
  token: string
  publicUrl: string
}

type IssueUploadUrlResult = {
  photoId: string
  bucket: string
  video: UploadUrlArtifact
  thumbnail: UploadUrlArtifact
}

export class SupabaseUploadGalleryVideoRepository {
  constructor(
    private readonly http: HttpPost,
    private readonly uploadToSignedUrl: UploadToSignedUrl,
    private readonly extractPosterFrame: ExtractVideoPosterFrame,
  ) {}

  execute: UploadGalleryVideo = async (params) => {
    try {
      params.onStage?.('generating')
      const poster = await this.extractPosterFrame(params.file)

      const { uploadUrl } = await this.http.post<
        {
          contentType: 'video/mp4' | 'video/quicktime'
          thumbnailContentType: 'image/webp'
          orgId?: string
          orgType?: string
          album?: 'dogs'
        },
        { uploadUrl: IssueUploadUrlResult }
      >({
        url: '/club-gallery-photos/upload-url',
        body: {
          contentType: params.file.type as 'video/mp4' | 'video/quicktime',
          thumbnailContentType: 'image/webp',
          orgId: params.orgId,
          orgType: params.orgType,
          album: params.album,
        },
      })

      params.onStage?.('uploading')
      await this.uploadToSignedUrl({
        bucket: uploadUrl.bucket,
        path: uploadUrl.video.storagePath,
        token: uploadUrl.video.token,
        file: params.file,
        signal: params.signal,
      })
      await this.uploadToSignedUrl({
        bucket: uploadUrl.bucket,
        path: uploadUrl.thumbnail.storagePath,
        token: uploadUrl.thumbnail.token,
        file: poster.blob,
        signal: params.signal,
      })

      params.onStage?.('recording')
      const { photo } = await this.http.post<
        Record<string, unknown>,
        { photo: GalleryPhoto }
      >({
        url: '/club-gallery-photos',
        body: {
          filename: params.file.name,
          filesize: params.file.size,
          mimetype: params.file.type,
          storagePath: uploadUrl.video.storagePath,
          cloudStorageUrl: uploadUrl.video.publicUrl,
          thumbnailPath: uploadUrl.thumbnail.storagePath,
          thumbnailUrl: uploadUrl.thumbnail.publicUrl,
          mediaType: 'video',
          durationSeconds: poster.durationSeconds,
          width: poster.width,
          height: poster.height,
          uploadedAt: new Date().toISOString(),
          caption: params.caption,
          orgId: params.orgId,
          orgType: params.orgType,
          album: params.album,
        },
      })

      return photo
    } catch (err) {
      mapServerError(err, {})
    }
  }
}
